'use client';

import { useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { IdentityBeam, ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { formatRelativeTime } from '@/lib/format-relative-time';
import type { TopBackerSummary } from '@/lib/launchpad/derive-fake-fields';
import {
  FEED_CARD_HOVER_BACKGROUND,
  FEED_CARD_OUTLINE_INTERACTION,
  FEED_CARD_RESTING_OUTLINE,
} from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';
import { ProposalBackerStack } from './proposal-backer-stack';
import { getProposalFeedGridClass } from './proposal-feed-grid';
import { ProposalFundingStatus } from './proposal-funding-status';
import { ProposalOutcomeCount } from './proposal-outcome-count';
import { ProposalProposerCell } from './proposal-proposer-cell';
import type { ProposalScore } from './proposal-score-badge';
import { ProposalScoreBadge } from './proposal-score-badge';
import type { ProposalFeedEventVariant, ProposalListItem } from './types';

export interface ProposalFeedRowProps {
  proposal: ProposalListItem;
  proposalScore: ProposalScore;
  topBackers: TopBackerSummary[];
  priorityImage?: boolean;
  animateValuesOnMount?: boolean;
  /** Event-page presentation: hides launchpad reactions and supplies the event's category glyph. */
  eventVariant?: ProposalFeedEventVariant;
  /** Hover-prefetch callback — fired on mouseenter+focus to warm the v2 SDK
   *  cache for the destination proposal-detail (or graduated market) page.
   *  Idempotent; safe to fire on every hover. Optional so the row still
   *  renders in test fixtures without a prefetch provider. */
  onHoverPrefetch?: (proposalPubkey: string) => void;
}

export function ProposalFeedRow({
  proposal,
  proposalScore,
  topBackers,
  priorityImage = false,
  animateValuesOnMount = true,
  eventVariant,
  onHoverPrefetch,
}: ProposalFeedRowProps): React.ReactNode {
  const tTable = useTranslations('launchpad.feed.table');
  const tTimeIntl = useTranslations('time');
  const locale = useLocale();
  const prefersReduced = useReducedMotion();

  const isGraduated = proposal.graduatedMarketPubkey !== null;
  const href = isGraduated
    ? `/markets/${proposal.graduatedMarketPubkey}`
    : `/proposals/${proposal.pubkey}`;
  // Hover prefetch only fires for the non-graduated case — graduated rows
  // open the markets detail page (different SDK hook + cache key); the
  // markets-page already wires its own prefetch on its cards.
  function handleHover(): void {
    if (!isGraduated) {
      onHoverPrefetch?.(proposal.pubkey);
    }
  }
  // Full route prefetch on intent. handleHover is a no-op for graduated rows
  // (it warms the proposal cache, not the markets cache they route to).
  const intent = useIntentRoutePrefetch(href, handleHover);
  const createdAtLabel = formatRelativeTime(
    proposal.createdAt,
    (key: string, params?: Record<string, number>): string =>
      tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params),
    { locale },
  );

  return (
    <IdentityBeam marketType={proposal.marketType} className="md:h-full">
      <div
        className={cn(
          getProposalFeedGridClass(),
          'relative flex flex-col items-stretch gap-3 rounded-card bg-background-primary px-4 py-4 md:min-h-0 md:h-full lg:grid lg:items-center lg:gap-8 lg:py-3',
          FEED_CARD_HOVER_BACKGROUND,
          FEED_CARD_RESTING_OUTLINE,
          prefersReduced !== true && FEED_CARD_OUTLINE_INTERACTION,
        )}
        data-testid="proposal-feed-row-root"
      >
        <Link
          href={href}
          prefetch={false}
          {...intent}
          aria-label={proposal.title}
          className="absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-primary"
        />
        <div className="flex min-w-0 items-center gap-3 lg:contents">
          {/* Cell 1: thumbnail + title */}
          <div className="flex min-w-0 flex-1 items-center gap-3 lg:col-start-1 lg:row-start-1">
            <ThumbWithOrigin marketType={proposal.marketType}>
              <OptimizedImage
                variant="thumbnail"
                entity={{
                  kind: 'market',
                  pubkey: proposal.pubkey,
                  category: String(proposal.category ?? '').toLowerCase(),
                  coverImageUrl:
                    proposal.coverImageUrl !== null && proposal.coverImageUrl !== ''
                      ? proposal.coverImageUrl
                      : null,
                  nsfw: (proposal as { coverImageNsfw?: boolean | null }).coverImageNsfw === true,
                }}
                alt=""
                size="size-12"
                rounded="rounded-overlay-item md:rounded-control"
                // Desktop keeps the inset cover used by the table row; compact
                // breakpoints retain the edge-to-edge card thumbnail.
                className="lg:box-border lg:p-1"
                imageClassName="lg:rounded-[calc(var(--radius-control)-0.25rem)]"
                // ALLOW-NO-PREVIEW: rendered inside a navigable row link; nested click targets would steal navigation
                preview={false}
                priority={priorityImage}
              />
            </ThumbWithOrigin>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <h3
                className="line-clamp-2 min-w-0 text-body font-medium text-foreground-primary"
                data-testid="proposal-feed-row-title"
                title={proposal.title}
              >
                {proposal.title}
              </h3>
              {createdAtLabel !== '' ? (
                <time
                  className="text-caption text-foreground-tertiary lg:hidden"
                  dateTime={proposal.createdAt}
                >
                  {createdAtLabel}
                </time>
              ) : null}
            </div>
          </div>

          {/* Cell 5 on desktop; header trailing badge on smaller screens. */}
          <div className="shrink-0 lg:col-start-5 lg:row-start-1 lg:flex lg:flex-col lg:gap-1">
            <ProposalScoreBadge score={proposalScore} />
            {createdAtLabel !== '' ? (
              <time
                className="hidden text-caption text-foreground-tertiary lg:block"
                dateTime={proposal.createdAt}
              >
                {createdAtLabel}
              </time>
            ) : null}
          </div>
        </div>

        {/* Cell 2: funding */}
        <div className="lg:col-start-2 lg:row-start-1">
          <ProposalFundingStatus
            totalFunded={proposal.totalStaked}
            creationThreshold={proposal.creationThreshold}
            animateValuesOnMount={animateValuesOnMount}
          />
        </div>

        <div className="flex min-w-0 items-center gap-4 lg:contents">
          {/* Cell 6: proposer */}
          <div className="min-w-0 flex-1 lg:col-start-6 lg:row-start-1">
            <ProposalProposerCell
              proposer={{
                walletAddress: proposal.creator,
                displayName: proposal.proposerDisplayName,
                handle: proposal.proposerHandle,
                avatarUrl: proposal.proposerAvatarUrl,
                avatarNsfw: proposal.proposerAvatarNsfw,
                avatarPreferences: proposal.proposerAvatarPreferences,
                connectedProviders: proposal.proposerConnectedProviders,
              }}
              partner={proposal.marketType === 'influencer'}
              partnerSocial={proposal.associatedCreator?.partnerSocial ?? null}
              disableNavigation
            />
          </div>

          {/* Cell 4: backers */}
          <div className="shrink-0 lg:col-start-4 lg:row-start-1">
            <ProposalBackerStack
              backers={topBackers}
              totalCount={proposal.backerCount}
              responsiveLabel={tTable('backersNoun', { count: proposal.backerCount })}
              animateValuesOnMount={animateValuesOnMount}
            />
          </div>
        </div>

        <div className="my-1 h-px w-full bg-border-primary lg:hidden" aria-hidden="true" />

        <div className="flex min-w-0 items-center justify-between gap-3 lg:contents">
          {/* Cell 3: category + outcomes */}
          <div className="relative z-10 min-w-0 flex-1 lg:col-start-3 lg:row-start-1">
            <ProposalOutcomeCount
              proposalPubkey={proposal.pubkey}
              category={proposal.category}
              outcomes={proposal.outcomes}
              {...(eventVariant ? { categoryIcon: eventVariant.categoryIcon } : {})}
            />
          </div>
        </div>
      </div>
    </IdentityBeam>
  );
}
